import React, { useEffect, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { BackButton } from "components/common/Buttons";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardFooter,
  Container,
  CardHeader,
  FormFeedback,
  InputGroup,
  Button,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import schema from "./schema";

import {
  createClientAdvance,
  getClientAdvance,
  updateClientAdvance,
} from "features/ClientAdvances/services/ClientAdvanceService";
import { ClientAdvance } from "features/ClientAdvances/types/client-advance.types";
import {
  ClientAdvanceSourceType,
  ClientAdvanceSourceTypeLabel,
  ClientAdvanceStatus,
  ClientAdvanceValueType,
  ClientAdvanceValueTypeLabel,
} from "features/ClientAdvances/constants/client-advance.constants";
import { formatCurrency } from "utils/currency";
import LinkedClientAdvancesTable from "components/common/LinkedClientAdvancesTable";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getAmc, getAmcOptions } from "features/Amc/services/AmcService";
import {
  AmcOption,
  AmcSingleValue,
} from "features/Amc/components/AmcSelectComponents";
import { AmcContractStatus } from "features/Amc/constants/amc-contract.constants";
import {
  getWorkOrder,
  getWorkOrderOptions,
} from "features/WorkOrders/services/WorkOrderService";
import {
  WorkOrderOption,
  WorkOrderSingleValue,
} from "features/WorkOrders/components/WorkOrderSelectComponents";
import { WorkOrderStatus } from "features/WorkOrders/constants/work-order.constants";
import {
  getPettyWork,
  getPettyWorkOptions,
} from "features/PettyWorks/services/PettyWorkService";
import {
  PettyWorkOption,
  PettyWorkSingleValue,
} from "features/PettyWorks/components/PettyWorkSelectComponents";
import { PettyWorkStatus } from "features/PettyWorks/constants/petty-work.constants";
import { getUser, getUserOptionsWithEmployeeProfile } from "features/Users/services/UserService";
import {
  buildUserSelectDefaultOption,
  userSelectWithAvatarComponents,
  type UserSelectOptionData,
} from "features/Users/components/UserSelectComponents";
import { getModeOfPaymentOptions } from "features/Meta/services/MetaService";
import { toast } from "react-toastify";
import { getDocumentTitle } from "utils/helpers";

const SOURCE_TYPE_OPTIONS = [
  { value: ClientAdvanceSourceType.AMC, label: "AMC" },
  { value: ClientAdvanceSourceType.WORK_ORDER, label: "Work Order" },
  { value: ClientAdvanceSourceType.PETTY_WORK, label: "Petty Work" },
];

function fetchOptionsForSourceType(sourceType: string) {
  if (sourceType === ClientAdvanceSourceType.WORK_ORDER) return getWorkOrderOptions;
  if (sourceType === ClientAdvanceSourceType.PETTY_WORK) return getPettyWorkOptions;
  return getAmcOptions;
}

function selectComponentsForSourceType(sourceType: string) {
  if (sourceType === ClientAdvanceSourceType.WORK_ORDER) {
    return { Option: WorkOrderOption, SingleValue: WorkOrderSingleValue };
  }
  if (sourceType === ClientAdvanceSourceType.PETTY_WORK) {
    return { Option: PettyWorkOption, SingleValue: PettyWorkSingleValue };
  }
  return { Option: AmcOption, SingleValue: AmcSingleValue };
}

/** Any Work Order / Petty Work status except COMPLETED/CANCELLED can still take an advance. */
const WORK_ORDER_ADVANCE_ELIGIBLE_STATUSES = [
  WorkOrderStatus.NEW,
  WorkOrderStatus.IN_PROGRESS,
  WorkOrderStatus.ON_HOLD,
  WorkOrderStatus.READY_FOR_BILLING,
].join(",");

const PETTY_WORK_ADVANCE_ELIGIBLE_STATUSES = [
  PettyWorkStatus.NEW,
  PettyWorkStatus.IN_PROGRESS,
  PettyWorkStatus.ON_HOLD,
  PettyWorkStatus.READY_FOR_BILLING,
].join(",");

/**
 * Only sources still eligible for a new advance — mirrors client-advance.policy.ts.
 * Work Order / Petty Work: any status except COMPLETED/CANCELLED. AMC: DRAFT only.
 */
function requestParamsForSourceType(sourceType: string) {
  if (sourceType === ClientAdvanceSourceType.WORK_ORDER) {
    return { status: WORK_ORDER_ADVANCE_ELIGIBLE_STATUSES };
  }
  if (sourceType === ClientAdvanceSourceType.PETTY_WORK) {
    return { status: PETTY_WORK_ADVANCE_ELIGIBLE_STATUSES };
  }
  return { status: AmcContractStatus.DRAFT };
}

/**
 * Fetches the selected source and resolves the amount an advance is capped
 * against. AMC uses `finalAmount` when set, else `agreedAmount` — mirrors
 * the backend's approval-time cap check.
 */
async function fetchSourceAgreedAmount(
  sourceType: string,
  sourceId: number,
): Promise<number | null> {
  if (sourceType === ClientAdvanceSourceType.WORK_ORDER) {
    const res = await getWorkOrder(sourceId);
    return res.success ? (res.data?.agreedAmount ?? null) : null;
  }
  if (sourceType === ClientAdvanceSourceType.PETTY_WORK) {
    const res = await getPettyWork(sourceId);
    return res.success ? (res.data?.agreedAmount ?? null) : null;
  }
  const res = await getAmc(sourceId);
  if (!res.success || !res.data) return null;
  return res.data.finalAmount ?? res.data.agreedAmount ?? null;
}

const ClientAdvanceForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(isEdit ? "Edit Client Advance" : "New Client Advance");

  const { id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState<ClientAdvance | null>(null);
  const [sourceAgreedAmount, setSourceAgreedAmount] = useState<number | null>(
    null,
  );
  const [sourceAgreedAmountLoading, setSourceAgreedAmountLoading] =
    useState(false);
  const [collectedByDefaultOption, setCollectedByDefaultOption] =
    useState<UserSelectOptionData | undefined>(undefined);

  const isLocked = !!record && record.status !== ClientAdvanceStatus.DRAFT;
  /** sourceId/sourceType are never editable after creation. */
  const isSourceLocked = isEdit;

  const lockedFormAlertMessage =
    record == null
      ? null
      : record.status === ClientAdvanceStatus.CANCELLED
        ? "This advance has been cancelled and can no longer be edited."
        : record.status !== ClientAdvanceStatus.DRAFT
          ? "This advance has already been received and can no longer be edited."
          : null;

  const handleSourceTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    validation.setFieldValue("sourceType", value);
    validation.setFieldValue("sourceId", "");
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      sourceType: record?.sourceType ?? ClientAdvanceSourceType.AMC,
      sourceId: record?.sourceId ?? "",

      valueType: record?.valueType ?? ClientAdvanceValueType.FIXED,
      value:
        record == null
          ? ""
          : (record.valueType ?? ClientAdvanceValueType.FIXED) ===
              ClientAdvanceValueType.PERCENTAGE
            ? (record.percentage ?? "")
            : (record.amount ?? ""),

      remarks: record?.remarks ?? "",
      purpose: record?.purpose ?? "",
      collectedByUserId: record?.collectedByUserId ?? "",
      isEdit,
      markAsReceived: true,
      modeOfPaymentId: "",
      paymentDate: "",
      referenceNumber: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (record && record.status !== ClientAdvanceStatus.DRAFT) {
        toast.error("This advance can no longer be edited.");
        return;
      }
      const n = Number(values.value);
      const valueType = values.valueType as ClientAdvanceValueType;
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("Enter a valid amount or percentage.");
        return;
      }
      const resolvedAmount =
        valueType === ClientAdvanceValueType.PERCENTAGE
          ? sourceAgreedAmount != null
            ? (sourceAgreedAmount * n) / 100
            : null
          : n;
      if (
        resolvedAmount != null &&
        sourceAgreedAmount != null &&
        resolvedAmount > sourceAgreedAmount
      ) {
        toast.error(
          `Advance amount (${formatCurrency(resolvedAmount)}) cannot exceed the agreed amount (${formatCurrency(sourceAgreedAmount)}).`,
        );
        return;
      }
      const remarks =
        values.remarks === "" || values.remarks == null ? null : String(values.remarks);
      const purpose =
        values.purpose === "" || values.purpose == null ? null : String(values.purpose);
      const collectedByUserId =
        values.collectedByUserId === "" || values.collectedByUserId == null
          ? null
          : Number(values.collectedByUserId);

      const amountField =
        valueType === ClientAdvanceValueType.FIXED ? { amount: n } : { percent: n };

      let response;
      try {
        if (isEdit) {
          response = await updateClientAdvance({
            id: Number(id),
            ...amountField,
            remarks,
            purpose,
            collectedByUserId,
          });
        } else {
          const markAsReceived = values.markAsReceived !== false;
          response = await createClientAdvance({
            sourceType: values.sourceType as ClientAdvanceSourceType,
            sourceId: Number(values.sourceId),
            ...amountField,
            remarks,
            purpose,
            collectedByUserId,
            markAsReceived,
            ...(markAsReceived
              ? {
                  modeOfPaymentId:
                    values.modeOfPaymentId === "" || values.modeOfPaymentId == null
                      ? null
                      : Number(values.modeOfPaymentId),
                  paymentDate: values.paymentDate || null,
                  referenceNumber: values.referenceNumber?.trim() || null,
                }
              : {}),
          });
        }
        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        }
      } catch (err) {
        toast.error((err as any).response?.data?.message ?? "Something went wrong.");
      }
    },
  });

  useEffect(() => {
    if (!id) return;

    const fetchRecord = async () => {
      try {
        const response = await getClientAdvance(Number(id));
        if (response.success) {
          setRecord(response.data);
        }
      } catch (err) {
        console.warn(err);
      }
    };

    fetchRecord();
  }, [id]);

  useEffect(() => {
    const collectorId = record?.collectedByUserId;
    if (collectorId == null || collectorId <= 0) {
      setCollectedByDefaultOption(undefined);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const response = await getUser(collectorId);
        if (!cancelled && response.success && response.data) {
          setCollectedByDefaultOption(
            buildUserSelectDefaultOption({
              ownerId: collectorId,
              owner: response.data,
            }),
          );
        }
      } catch (err) {
        console.warn(err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [record?.collectedByUserId]);

  const selectedSourceType = validation.values.sourceType;
  const selectedSourceId = validation.values.sourceId;

  useEffect(() => {
    const sourceId = Number(selectedSourceId);
    if (!Number.isFinite(sourceId) || sourceId <= 0) {
      setSourceAgreedAmount(null);
      setSourceAgreedAmountLoading(false);
      return;
    }

    let cancelled = false;
    setSourceAgreedAmountLoading(true);
    void (async () => {
      try {
        const amount = await fetchSourceAgreedAmount(
          selectedSourceType,
          sourceId,
        );
        if (!cancelled) setSourceAgreedAmount(amount);
      } catch (err) {
        if (!cancelled) {
          console.warn(err);
          setSourceAgreedAmount(null);
        }
      } finally {
        if (!cancelled) setSourceAgreedAmountLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedSourceType, selectedSourceId]);

  const hasSelectedSource =
    selectedSourceId !== "" &&
    Number.isFinite(Number(selectedSourceId)) &&
    Number(selectedSourceId) > 0;
  const sourceAgreedAmountMissing =
    hasSelectedSource &&
    !sourceAgreedAmountLoading &&
    (sourceAgreedAmount == null || sourceAgreedAmount <= 0);

  const resolvedAdvanceAmount =
    validation.values.value !== "" &&
    !Number.isNaN(Number(validation.values.value))
      ? validation.values.valueType === ClientAdvanceValueType.PERCENTAGE
        ? sourceAgreedAmount != null
          ? (sourceAgreedAmount * Number(validation.values.value)) / 100
          : null
        : Number(validation.values.value)
      : null;
  const advanceExceedsAgreedAmount =
    resolvedAdvanceAmount != null &&
    sourceAgreedAmount != null &&
    resolvedAdvanceAmount > sourceAgreedAmount;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={isEdit ? "Edit Client Advance" : "New Client Advance"}
            pageTitle="Finance"
            items={[
              {
                label: "Client Advances",
                path: "/finance/client-advances",
              },
            ]}
          />
          <Form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row className="justify-content-center">
              <Col xl={12} lg={10}>
                <Card className="shadow-none border">
                  <CardHeader className="border-bottom bg-transparent pb-3">
                    <h5 className="card-title mb-1">
                      {isEdit ? "Edit client advance" : "Create client advance"}
                    </h5>
                    <p className="text-muted mb-0 small">
                      Link an AMC, Work Order, or Petty Work booking, set a fixed
                      rupee value or an AMC percentage. By default the advance is
                      received immediately — uncheck "Mark as received" below to
                      log it before the money has actually arrived.
                    </p>
                  </CardHeader>
                  <CardBody className="pt-4">
                    {lockedFormAlertMessage ? (
                      <Alert color="warning" className="mb-4" role="alert">
                        <i className="ri-information-line me-2 align-middle" />
                        {lockedFormAlertMessage}
                      </Alert>
                    ) : null}
                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Source
                    </div>
                    <Row className="g-3 mb-2">
                      <Col md={6}>
                        <Label className="form-label">Source type</Label>
                        <div className="d-flex gap-4 pt-1 flex-wrap">
                          {SOURCE_TYPE_OPTIONS.map((opt) => (
                            <div className="form-check" key={opt.value}>
                              <Input
                                type="radio"
                                className="form-check-input"
                                id={`source-type-${opt.value}`}
                                name="sourceType"
                                value={opt.value}
                                checked={validation.values.sourceType === opt.value}
                                onChange={handleSourceTypeChange}
                                onBlur={validation.handleBlur}
                                disabled={isSourceLocked}
                              />
                              <Label
                                className="form-check-label"
                                for={`source-type-${opt.value}`}
                              >
                                {opt.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                        {validation.errors.sourceType &&
                          (validation.touched.sourceType || validation.submitCount > 0) && (
                            <FormFeedback type="invalid" className="d-block">
                              {validation.errors.sourceType}
                            </FormFeedback>
                          )}

                        <div className="mt-3">
                          <Label className="form-label" for="client-advance-source-entity">
                            {ClientAdvanceSourceTypeLabel[
                              validation.values.sourceType as ClientAdvanceSourceType
                            ] ?? "Source"}
                          </Label>
                          {isSourceLocked ? (
                            <Input
                              type="text"
                              className="form-control"
                              value={
                                record?.source?.code ??
                                (record?.sourceId ? `#${record.sourceId}` : "")
                              }
                              readOnly
                              disabled
                            />
                          ) : (
                            <>
                              <AsyncPaginatedSelect
                                key={validation.values.sourceType}
                                fetchOptions={fetchOptionsForSourceType(
                                  validation.values.sourceType,
                                )}
                                components={selectComponentsForSourceType(
                                  validation.values.sourceType,
                                )}
                                requestParams={requestParamsForSourceType(
                                  validation.values.sourceType,
                                )}
                                placeholder="Search…"
                                value={validation.values.sourceId}
                                onChange={(val: any) => {
                                  validation.setFieldValue("sourceId", val?.value);
                                }}
                                onBlur={() => validation.setFieldTouched("sourceId", true)}
                                isInvalid={
                                  validation.touched.sourceId && !!validation.errors.sourceId
                                }
                              />
                              {validation.errors.sourceId &&
                                (validation.touched.sourceId || validation.submitCount > 0) && (
                                  <FormFeedback type="invalid" className="d-block">
                                    {validation.errors.sourceId}
                                  </FormFeedback>
                                )}
                              <small className="text-muted d-block mt-1">
                                {selectedSourceType === ClientAdvanceSourceType.AMC
                                  ? "Only DRAFT AMCs are listed here — once ready for activation, that AMC can no longer accept a new advance."
                                  : "Only sources still eligible for an advance are listed here — a Work Order or Petty Work can accept advances at any stage until it's completed or cancelled."}
                              </small>
                            </>
                          )}
                        </div>

                        {hasSelectedSource && !sourceAgreedAmountLoading ? (
                          <div className="mt-3">
                            <Label className="form-label">
                              Agreed amount
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              value={
                                sourceAgreedAmount != null &&
                                sourceAgreedAmount > 0
                                  ? formatCurrency(sourceAgreedAmount)
                                  : "—"
                              }
                              readOnly
                              disabled
                            />
                            {sourceAgreedAmountMissing ? (
                              <Alert
                                color="warning"
                                className="mb-0 mt-2 py-2 px-3"
                              >
                                <i className="ri-error-warning-line align-middle me-1" />
                                This source has no agreed amount set — add
                                line items (or, for an AMC, set a final
                                amount) before it can accept an advance.
                              </Alert>
                            ) : null}
                            <small className="text-muted d-block mt-2">
                              The agreed amount should cover this advance —
                              together with any existing advances already
                              raised against this source.
                            </small>

                            <LinkedClientAdvancesTable
                              className="mt-3"
                              sourceType={
                                selectedSourceType as ClientAdvanceSourceType
                              }
                              sourceId={selectedSourceId}
                              excludeAdvanceId={
                                isEdit && id ? Number(id) : null
                              }
                              heading="Existing advances against this source"
                            />
                          </div>
                        ) : null}
                      </Col>
                    </Row>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Value
                    </div>
                    <Row>
                      <Col lg={8}>
                        <Label className="form-label" for="client-advance-value-input">
                          {validation.values.valueType === ClientAdvanceValueType.PERCENTAGE
                            ? "Percentage"
                            : "Amount (INR)"}
                        </Label>
                        <InputGroup>
                          <Input
                            id="client-advance-value-input"
                            type="number"
                            className="form-control"
                            placeholder={
                              validation.values.valueType === ClientAdvanceValueType.PERCENTAGE
                                ? "e.g. 10"
                                : "0.00"
                            }
                            name="value"
                            step="0.01"
                            value={validation.values.value}
                            onChange={(e) => {
                              const raw = e.target.value;
                              validation.setFieldValue("value", raw === "" ? "" : Number(raw));
                            }}
                            onBlur={validation.handleBlur}
                            invalid={Boolean(
                              (validation.errors.value &&
                                (validation.touched.value ||
                                  validation.submitCount > 0)) ||
                                advanceExceedsAgreedAmount,
                            )}
                            disabled={isLocked}
                          />
                          <Input
                            type="select"
                            className="form-select"
                            style={{ maxWidth: "11rem" }}
                            name="valueType"
                            value={validation.values.valueType}
                            onChange={(e) => {
                              validation.setFieldValue(
                                "valueType",
                                e.target.value as ClientAdvanceValueType,
                              );
                              validation.setFieldTouched("value", true);
                            }}
                            onBlur={validation.handleBlur}
                            disabled={isLocked}
                          >
                            {Object.values(ClientAdvanceValueType).map((vt) => (
                              <option key={vt} value={vt}>
                                {ClientAdvanceValueTypeLabel[vt]}
                              </option>
                            ))}
                          </Input>
                        </InputGroup>
                        <small className="text-muted d-block mt-2">
                          Fixed is a rupee amount. Percentage is resolved
                          against the source's agreed amount on save.
                        </small>
                        {validation.values.valueType ===
                          ClientAdvanceValueType.PERCENTAGE &&
                        resolvedAdvanceAmount != null && (
                          <small className="text-muted d-block mt-1">
                            Preview from source's agreed amount:{" "}
                            <span className="text-body fw-medium">
                              {formatCurrency(resolvedAdvanceAmount)}
                            </span>
                          </small>
                        )}
                        {validation.errors.value &&
                          (validation.touched.value || validation.submitCount > 0) && (
                            <FormFeedback type="invalid" className="d-block">
                              {validation.errors.value}
                            </FormFeedback>
                          )}
                        {advanceExceedsAgreedAmount ? (
                          <FormFeedback type="invalid" className="d-block">
                            Advance amount ({formatCurrency(resolvedAdvanceAmount ?? 0)})
                            cannot exceed the agreed amount (
                            {formatCurrency(sourceAgreedAmount ?? 0)}).
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Collection
                    </div>
                    <Row className="g-3 mb-2">
                      <Col md={6}>
                        <Label className="form-label">
                          Collected by <span className="text-danger">*</span>
                        </Label>
                        <AsyncPaginatedSelect
                          fetchOptions={getUserOptionsWithEmployeeProfile}
                          components={userSelectWithAvatarComponents}
                          placeholder="Search users…"
                          defaultOption={collectedByDefaultOption}
                          value={validation.values.collectedByUserId}
                          onChange={(val: any) =>
                            validation.setFieldValue("collectedByUserId", val?.value ?? "")
                          }
                          onBlur={() => validation.setFieldTouched("collectedByUserId", true)}
                          isInvalid={
                            (validation.touched.collectedByUserId ||
                              validation.submitCount > 0) &&
                            !!validation.errors.collectedByUserId
                          }
                          isDisabled={isLocked}
                        />
                        {(validation.touched.collectedByUserId ||
                          validation.submitCount > 0) &&
                        validation.errors.collectedByUserId ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.collectedByUserId}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>

                    {!isEdit ? (
                      <>
                        <hr className="my-4 text-muted opacity-25" />
                        <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                          Disbursement payment
                        </div>
                        <div className="form-check mb-3">
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            id="client-advance-mark-as-received"
                            checked={validation.values.markAsReceived}
                            onChange={(e) =>
                              validation.setFieldValue("markAsReceived", e.target.checked)
                            }
                          />
                          <Label
                            className="form-check-label"
                            for="client-advance-mark-as-received"
                          >
                            Mark as received
                          </Label>
                          <p className="text-muted small mb-0">
                            {validation.values.markAsReceived
                              ? "The payment is created immediately along with the advance."
                              : "Recorded as Awaiting Receipt — no payment yet. Use \"Receive\" on the advance once the money arrives."}
                          </p>
                        </div>
                        {validation.values.markAsReceived ? (
                          <Row className="g-3 mb-2">
                            <Col md={6}>
                              <Label className="form-label">
                                Mode of payment <span className="text-danger">*</span>
                              </Label>
                              <AsyncPaginatedSelect
                                fetchOptions={getModeOfPaymentOptions}
                                placeholder="Search modes…"
                                value={validation.values.modeOfPaymentId}
                                onChange={(val: any) =>
                                  validation.setFieldValue("modeOfPaymentId", val?.value ?? "")
                                }
                                onBlur={() =>
                                  validation.setFieldTouched("modeOfPaymentId", true)
                                }
                                isInvalid={
                                  (validation.touched.modeOfPaymentId ||
                                    validation.submitCount > 0) &&
                                  !!validation.errors.modeOfPaymentId
                                }
                              />
                              {(validation.touched.modeOfPaymentId ||
                                validation.submitCount > 0) &&
                                validation.errors.modeOfPaymentId && (
                                  <FormFeedback type="invalid" className="d-block">
                                    {validation.errors.modeOfPaymentId}
                                  </FormFeedback>
                                )}
                            </Col>
                            <Col md={6}>
                              <Label className="form-label" for="client-advance-payment-date">
                                Payment date <span className="text-muted fw-normal">(optional)</span>
                              </Label>
                              <Flatpickr
                                id="client-advance-payment-date"
                                className="form-control"
                                placeholder="Select date"
                                options={{ altInput: true, altFormat: "d M, Y", dateFormat: "d M, Y" }}
                                onChange={(date: any) =>
                                  validation.setFieldValue(
                                    "paymentDate",
                                    date[0] ? moment(date[0]).format("YYYY-MM-DD") : "",
                                  )
                                }
                                value={
                                  validation.values.paymentDate
                                    ? new Date(validation.values.paymentDate)
                                    : ""
                                }
                              />
                            </Col>
                            <Col md={6}>
                              <Label className="form-label" for="client-advance-reference-number">
                                Reference number{" "}
                                <span className="text-muted fw-normal">(optional)</span>
                              </Label>
                              <Input
                                id="client-advance-reference-number"
                                type="text"
                                className="form-control"
                                placeholder="UTR, cheque no., transaction id…"
                                name="referenceNumber"
                                value={validation.values.referenceNumber}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                              />
                            </Col>
                          </Row>
                        ) : null}
                      </>
                    ) : null}

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Purpose
                    </div>
                    <Row className="mb-4">
                      <Col>
                        <Label className="form-label" for="client-advance-purpose">
                          Purpose <span className="text-muted fw-normal">(optional)</span>
                        </Label>
                        <Input
                          id="client-advance-purpose"
                          type="textarea"
                          className="form-control"
                          rows={2}
                          placeholder="Why this advance is being raised…"
                          name="purpose"
                          value={validation.values.purpose}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          disabled={isLocked}
                        />
                      </Col>
                    </Row>

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Remarks
                    </div>
                    <Row>
                      <Col>
                        <Label className="form-label visually-hidden" for="client-advance-remarks">
                          Remarks
                        </Label>
                        <Input
                          id="client-advance-remarks"
                          type="textarea"
                          className="form-control"
                          rows={3}
                          placeholder="Optional notes for finance…"
                          name="remarks"
                          value={validation.values.remarks}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          disabled={isLocked}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                  <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                    <BackButton />
                    <Button
                      type="submit"
                      color="primary"
                      className="btn-label px-4"
                      disabled={
                        isLocked ||
                        validation.isSubmitting ||
                        advanceExceedsAgreedAmount
                      }
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          {isEdit ? "Updating…" : "Submitting…"}
                        </>
                      ) : isEdit ? (
                        "Save changes"
                      ) : (
                        "Create advance"
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ClientAdvanceForm;
